import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Card, Field, Pill } from '../../shared/ui';
import { ApiError } from '../../shared/lib/openedx';
import { domainsApi, type Domain } from '../api/domains';

const TERMINAL_STATES = new Set(['live', 'released']);
const STALE_AFTER_MS = 2 * 60_000;

function humanize(value: string | null) {
  if (!value) return 'Pending';
  return value.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase());
}

function formatTime(value: string | null) {
  if (!value) return 'Not yet';
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleString();
}

function fact(value: boolean | null) {
  if (value === null) return 'Pending';
  return value ? 'Ready' : 'Not ready';
}

function stateTone(domain: Domain): 'active' | 'planned' | 'suspended' | 'retired' {
  if (domain.state === 'live') return 'active';
  if (domain.state === 'released') return 'retired';
  if (domain.last_error_code) return 'suspended';
  return 'planned';
}

function errorMessage(error: unknown, fallback: string) {
  if (!(error instanceof ApiError)) return fallback;
  const body = error.body as { detail?: string; hostname?: string[] | string } | null;
  if (body?.detail) return body.detail;
  if (Array.isArray(body?.hostname)) return body.hostname.join(' ');
  if (body?.hostname) return body.hostname;
  if (error.status === 409) return 'That hostname is already assigned to another tenant.';
  return fallback;
}

export function DomainsCard({ slug }: { slug: string }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [hostname, setHostname] = useState('');
  const [hostnameError, setHostnameError] = useState('');

  const domainsQuery = useQuery({
    queryKey: ['tenant-domains', slug],
    queryFn: () => domainsApi.list(slug),
    refetchInterval: (query) => {
      const domains = query.state.data;
      return domains?.some((domain) => !TERMINAL_STATES.has(domain.state)) ? 5_000 : false;
    },
  });

  const create = useMutation({
    mutationFn: (value: string) => domainsApi.create(slug, value),
    onSuccess: () => {
      setHostname('');
      setHostnameError('');
      setAdding(false);
      queryClient.invalidateQueries({ queryKey: ['tenant-domains', slug] });
    },
  });

  const check = useMutation({
    mutationFn: (id: number) => domainsApi.check(slug, id),
    onSuccess: (updated) => {
      queryClient.setQueryData<Domain[]>(['tenant-domains', slug], (domains = []) =>
        domains.map((domain) => (domain.id === updated.id ? updated : domain)),
      );
    },
  });

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const normalized = hostname.trim().toLowerCase().replace(/\.$/, '');
    if (!/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/.test(normalized)) {
      setHostnameError('Enter a hostname such as learn.customer.com, without https:// or a path.');
      return;
    }
    setHostnameError('');
    create.mutate(normalized);
  }

  return (
    <Card
      title="Domains"
      action={!adding ? <Button onClick={() => setAdding(true)}>Add domain</Button> : undefined}
    >
      {domainsQuery.isError && (
        <Banner tone="danger">
          <strong>Domain status could not be loaded.</strong> Refresh the page to try again.
        </Banner>
      )}

      {adding && (
        <form className="console__domain-form" onSubmit={submit}>
          <Field
            label="Custom hostname"
            name="custom-domain"
            placeholder="learn.customer.com"
            value={hostname}
            error={hostnameError}
            disabled={create.isPending}
            autoComplete="off"
            onChange={(event) => setHostname(event.target.value)}
          />
          {create.isError && (
            <Banner tone="danger">
              <strong>That domain was not added.</strong>{' '}
              {errorMessage(create.error, 'Check the hostname and try again.')}
            </Banner>
          )}
          <div className="console__form-actions">
            <Button type="submit" variant="primary" busy={create.isPending}>Add domain</Button>
            <Button
              type="button"
              variant="ghost"
              disabled={create.isPending}
              onClick={() => {
                setAdding(false);
                setHostnameError('');
                create.reset();
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {domainsQuery.isLoading && <p className="console__hint">Loading domain status…</p>}
      {domainsQuery.data?.length === 0 && !adding && (
        <p className="console__hint">No domains are registered for this tenant.</p>
      )}

      <div className="console__domains">
        {domainsQuery.data?.map((domain) => (
          <DomainRow
            key={domain.id}
            domain={domain}
            checking={check.isPending && check.variables === domain.id}
            checkError={check.isError && check.variables === domain.id ? check.error : null}
            onCheck={() => check.mutate(domain.id)}
          />
        ))}
      </div>
    </Card>
  );
}

function DomainRow({
  domain,
  checking,
  checkError,
  onCheck,
}: {
  domain: Domain;
  checking: boolean;
  checkError: unknown;
  onCheck: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const observedAt = domain.observed_at ? new Date(domain.observed_at).valueOf() : null;
  const stale = domain.desired_state === 'active' && domain.state !== 'live'
    && (!observedAt || Number.isNaN(observedAt) || Date.now() - observedAt > STALE_AFTER_MS);
  const events = [...domain.events].sort(
    (left, right) => new Date(right.created_at).valueOf() - new Date(left.created_at).valueOf(),
  ).slice(0, 6);

  async function copyInstruction() {
    await navigator.clipboard.writeText(`CNAME ${domain.hostname} ${domain.expected_cname}`);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2_000);
  }

  return (
    <section className="console__domain" aria-labelledby={`domain-${domain.id}`}>
      <div className="console__domain-head">
        <div>
          <div className="console__domain-title">
            <h3 id={`domain-${domain.id}`}>{domain.hostname}</h3>
            <span className="console__inherited">{domain.kind}</span>
          </div>
          <Pill tone={stateTone(domain)}>{humanize(domain.state)}</Pill>
        </div>
        <Button busy={checking} disabled={domain.desired_state === 'released'} onClick={onCheck}>
          Check now
        </Button>
      </div>

      {stale && (
        <Banner tone="warning">
          <strong>Status may be stale.</strong> The last successful observation was {formatTime(domain.observed_at)}.
        </Banner>
      )}
      {checkError !== null && (
        <Banner tone="danger">
          <strong>The check did not start.</strong> {errorMessage(checkError, 'Try again in a moment.')}
        </Banner>
      )}
      {domain.last_error_code && (
        <Banner tone="danger">
          <strong>{humanize(domain.last_error_code)}.</strong>{' '}
          {domain.last_error_message ?? 'The next automatic check will try again.'}
        </Banner>
      )}

      {domain.kind === 'custom' && domain.expected_cname && (
        <div className="console__dns-instruction">
          <div>
            <span>Required DNS record</span>
            <code>CNAME {domain.hostname} → {domain.expected_cname}</code>
          </div>
          <Button variant="ghost" onClick={copyInstruction}>{copied ? 'Copied' : 'Copy'}</Button>
        </div>
      )}

      <dl className="console__domain-facts">
        <dt>DNS</dt><dd>{humanize(domain.dns_status)}</dd>
        <dt>Observed DNS</dt><dd className="mono">{domain.observed_dns.join(', ') || 'None'}</dd>
        <dt>Route</dt><dd>{fact(domain.ingress_admitted)}</dd>
        <dt>Certificate</dt>
        <dd>
          {fact(domain.certificate_ready)}
          {domain.certificate_reason ? ` · ${domain.certificate_reason}` : ''}
        </dd>
        <dt>Certificate expiry</dt><dd>{formatTime(domain.cert_not_after)}</dd>
        <dt>ACME path</dt><dd>{fact(domain.acme_path_reachable)}</dd>
        <dt>HTTPS</dt><dd>{fact(domain.reachable)}</dd>
        <dt>Last checked</dt><dd>{formatTime(domain.observed_at)}</dd>
        <dt>Next retry</dt><dd>{formatTime(domain.next_retry_at)}</dd>
        <dt>Retries</dt><dd>{domain.retry_count}</dd>
      </dl>

      {events.length > 0 && (
        <div className="console__domain-activity">
          <h4>Recent activity</h4>
          <ol>
            {events.map((event) => (
              <li key={event.id}>
                <time dateTime={event.created_at}>{formatTime(event.created_at)}</time>
                <span><strong>{humanize(event.event_type)}</strong> {event.message}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}
